import {
  ComponentComplianceOUT,
  ComponentCreateIN,
  ComponentCreateOUT,
  ComponentDetailsByFileOut,
  ComponentInsightsOUT,
  ComponentReferenceUnitEnum,
  EPDGroupsCheckOUT,
  ReferenceUnitChoices,
  componentInsightsApi,
  componentsApi,
  getLocalAuthHeader,
  GraphicOrientation,
} from "api-client";
import { TagsOption } from "components/TagsInput";
import { REFERENCE_UNITS } from "features/MappingEditor/consts";
import { makeAutoObservable, runInAction } from "mobx";
import { BGItem } from "components/ButtonGroups";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import {
  OptionsList,
  Property,
  PropertyValue,
  BPEStore,
} from "features/BuildingProductEditor/ProductEditorStore";
import { searchStore } from "features/ProductSearch/SearchStore";
import { buildingStore } from "store/Building/BuildingStore";
import { ChildProps } from "./features/AddedChild";

interface Tag {
  id: string;
  name: string;
}

export interface CustomComponent {
  id: string;
  name: string;
  auto_mapping_tags: Tag[];
  reference_unit: BGItem;
  childrenList: ChildProps[];
  preuse_qualification?: string;
  deconstructability_qualification: string;
  reusability_qualification?: string;
  compliance?: ComponentComplianceOUT;
  din_category?: string;
  properties: Property[];
  lifespan: undefined | number;
  thickness: undefined | number | string;
}

const initialData: CustomComponent = {
  id: "",
  name: "",
  auto_mapping_tags: [],
  reference_unit: REFERENCE_UNITS[2],
  childrenList: [],
  preuse_qualification: "",
  deconstructability_qualification: "",
  reusability_qualification: "",
  compliance: undefined,
  din_category: "",
  properties: [],
  lifespan: undefined,
  thickness: undefined,
};

class ComponentsStore {
  data: CustomComponent = initialData;
  openedSearchInCE = false;
  optionsLists: OptionsList = {
    preuse_qualification: [],
    reusability_qualification: [],
  };
  onTheFlyProductAdded: ComponentDetailsByFileOut | undefined = undefined;
  productSpecifications: EPDGroupsCheckOUT | undefined;
  saveValidationErrors = true;
  componentInsights: ComponentInsightsOUT | null = null;
  graphicOrientation: GraphicOrientation = "horizontal";
  diagramToUpload: File | undefined = undefined;
  uploadedDiagramIDs: string[] = [];

  setData(data: CustomComponent) {
    this.data = data;
  }

  setSaveValidationErrors(saveValidationErrors: boolean) {
    this.saveValidationErrors = saveValidationErrors;
  }

  setOnTheFlyProductAdded(
    onTheFlyProductAdded: ComponentDetailsByFileOut | undefined
  ) {
    this.onTheFlyProductAdded = onTheFlyProductAdded;
  }

  setProductSpecifications(productSpecifications: EPDGroupsCheckOUT) {
    this.productSpecifications = productSpecifications;
  }

  setComponentInsights(componentInsights: ComponentInsightsOUT) {
    this.componentInsights = componentInsights;
  }

  setGraphicOrientation(orientation: "vertical" | "horizontal") {
    this.graphicOrientation = orientation;
  }

  resetData() {
    this.data = initialData;
    this.productSpecifications = undefined;
    this.saveValidationErrors = true;
    this.diagramToUpload = undefined;
  }

  onChangeData(
    name: string,
    value:
      | string
      | boolean
      | []
      | undefined
      | Tag[]
      | ComponentDetailsByFileOut[]
      | ComponentComplianceOUT
      | BGItem
      | null
  ) {
    runInAction(() => {
      if (name) this.data[name as keyof CustomComponent] = value as never;
    });
    this.checkSaveValidation();
  }

  onChangeProperties(value: Property[]) {
    runInAction(() => {
      this.data.properties = value as [];
    });
  }

  updatePropertyValues(
    index: number,
    key: keyof Property,
    value: PropertyValue
  ) {
    const propertiesList = [...this.data.properties];
    runInAction(() => {
      if (key === "slug") {
        propertiesList[index].slug = value as string;
        propertiesList[index].id = value as string;
      } else if (key === "value") {
        propertiesList[index].value = value;
      } else if (key === "name") {
        propertiesList[index].name = value as string;
      }
    });
    this.onChangeProperties(propertiesList);
  }

  getValueFromObject(key: string, isEng?: boolean) {
    const value = Object.entries(this.data).find(
      (item) => item[0] === key
    )?.[1];
    if (key === "tags_automapper") {
      return value?.map((item: TagsOption) => item.name).join(", ");
    } else if (key === "reference_unit") {
      return isEng ? value?.name : value?.name_de;
    } else if (BPEStore.isAnItemOfOptions(key)) {
      return BPEStore.optionsLists[key]?.find((item) => item.id === value)
        ?.name;
    } else {
      return value;
    }
  }

  checkSaveValidation() {
    const validity =
      this.data.name &&
      this.data.reference_unit &&
      this.data.childrenList?.length > 1 &&
      this.data.childrenList?.filter(
        (item) => item.unit_conversion && Number(item.unit_conversion) > 0
      ).length === this.data.childrenList?.length;
    this.setSaveValidationErrors(!validity);
  }

  async addChildItem(item: ComponentDetailsByFileOut) {
    this.onChangeData("childrenList", [...this.data.childrenList, item]);
    await this.getComponentCompliance();
  }

  onChangeChildParams(
    key: keyof ChildProps,
    index: number,
    value?: string | number | null
  ) {
    runInAction(() => {
      this.data.childrenList[index] = {
        ...this.data.childrenList[index],
        [key]: value,
      };
      this.checkSaveValidation();
    });
  }

  setOpenedSearchInCE(openedSearchInCE: boolean) {
    this.openedSearchInCE = openedSearchInCE;
  }

  setDiagramsToUpload(diagramToUpload: File | undefined) {
    this.diagramToUpload = diagramToUpload;
  }

  setUploadedDiagramIDs(uploadedDiagramIDs: string[]) {
    this.uploadedDiagramIDs = uploadedDiagramIDs;
  }

  async getComponentCompliance() {
    if (!this.data.childrenList?.length) {
      this.onChangeData("compliance", undefined);
      return;
    }
    const authHeader = getLocalAuthHeader();
    const productIds = this.data.childrenList.map((item) => item.id);
    await componentsApi
      .inventoryApiV1RoutersComponentsCheckComponentCompliance(
        {
          product_ids: productIds,
        },
        authHeader
      )
      .then((response) => this.onChangeData("compliance", response.data));
  }

  private prepareData() {
    const orgProperties = this.data.properties.map((item) => {
      return {
        [item.slug]: {
          name: item.name,
          property_type: item.property_type,
          value:
            item.property_type === "number"
              ? item.value
                ? Number(item.value)
                : ""
              : item.value ?? "",
        },
      };
    });

    const props: ComponentCreateIN = {
      name: this.data.name,
      tags_automapper: this.data.auto_mapping_tags.map((item) => item.name),
      reference_unit: this.data.reference_unit
        ?.id as ComponentReferenceUnitEnum,
      children: this.data.childrenList.map((item, index) => {
        return {
          product_id: item.id,
          unit_conversion: Number(item.unit_conversion),
          comment: item.comment,
          component_order: index + 1,
          thickness: Number(item.thickness),
          life_span: Number(item.user_lifespan),
        };
      }),
      building: searchStore.usingOrganisationSearch
        ? null
        : buildingStore.buildingID,
      organisation_id: String(sidebarMenuStore.currentOrganisation?.id),
      din_category_id: this.data.din_category || null,
      org_properties: Object.assign({}, ...orgProperties),
      file_ids: this.uploadedDiagramIDs,
    };
    return props;
  }

  async registerComponent(): Promise<ComponentCreateOUT | void> {
    const props: ComponentCreateIN = this.prepareData();
    const authHeader = getLocalAuthHeader();
    return await componentsApi
      .inventoryApiV1RoutersComponentsCreateComponent(props, authHeader)
      .then((response) => {
        if (response.data) return response.data;
      });
  }

  async updateComponent() {
    const props: ComponentCreateIN = this.prepareData();
    const authHeader = getLocalAuthHeader();
    return await componentsApi.inventoryApiV1RoutersComponentsEditComponent(
      this.data.id,
      props,
      authHeader
    );
  }

  async deleteComponent(component_id: string) {
    const authHeader = getLocalAuthHeader();
    return await componentsApi.inventoryApiV1RoutersComponentsDeleteComponent(
      component_id,
      authHeader
    );
  }

  async upgradeComponent() {
    const authHeader = getLocalAuthHeader();
    return await componentsApi.inventoryApiV1RoutersComponentsUpgradeComponent(
      this.data.id,
      authHeader
    );
  }

  async fetchComponentInsights(orientation?: GraphicOrientation | undefined) {
    await componentInsightsApi()
      .buildingProductsApiV1RoutersComponentInsightsComponentInsights({
        reference_unit: this.data.reference_unit.id as ReferenceUnitChoices,
        orientation: orientation ? orientation : this.graphicOrientation,
        parts: this.data.childrenList
          .filter((rec) => ["M2", "M3"].includes(rec.reference_unit))
          .map((item) => ({
            product_id: item.id,
            thickness: Number(item.thickness),
            factor: Number(item.unit_conversion),
          })),
      })
      .then((response) => {
        if (orientation) this.setGraphicOrientation(orientation);
        this.setComponentInsights(response.data);
      })
      .catch((err) => console.error(err, "ComponentInsights"));
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: ComponentsStore;
  static getInstance(): ComponentsStore {
    if (!ComponentsStore.instance) {
      ComponentsStore.instance = new ComponentsStore();
    }
    return ComponentsStore.instance;
  }
}

export const COEStore = ComponentsStore.getInstance();
